// Chefi Super as a plan: who is in it, invites, gifts bought, redeeming codes. Kept in memory and loaded
// from the server (/api/plan) when the Super screen opens; settings.isSuper stays the offline cache.
import { create } from 'zustand';
import i18n from '../i18n';
import type { PlanInfo, PlanProduct } from '../types';
import * as auth from '../platform/auth';
import { checkEntitlement } from '../platform/iap';
import { useSettings } from './settings';

type Redeemed = { joined?: boolean; gift?: { product: PlanProduct; months: number; until: number } };

type PlanState = {
  info: PlanInfo | null;
  busy: boolean;
  /** Key under plan.errors, or null. */
  error: string | null;
  load: () => Promise<void>;
  invite: () => Promise<{ code: string; url: string } | null>;
  remove: (memberId: string) => Promise<void>;
  leave: () => Promise<void>;
  redeem: (code: string) => Promise<Redeemed | null>;
  /** After a purchase, a join or a redeem: reload the plan and the Super cache. */
  refresh: () => Promise<void>;
  /** Back from a web checkout: waits (up to ~30 s) until the server shows the new plan or gift. */
  waitForPayment: () => Promise<boolean>;
  confirming: boolean;
};

const errorOf = (e: unknown) => {
  const code = e instanceof auth.ApiError ? e.code : 'generic';
  return i18n.exists(`plan.errors.${code}`) ? code : 'generic';
};

export const usePlan = create<PlanState>((set, get) => {
  async function run<T>(work: () => Promise<T>): Promise<T | null> {
    set({ busy: true, error: null });
    try {
      return await work();
    } catch (e) {
      set({ error: errorOf(e) });
      return null;
    } finally {
      set({ busy: false });
    }
  }
  return {
    info: null,
    busy: false,
    confirming: false,
    error: null,
    load: async () => {
      const info = await auth.fetchPlan().catch(() => null);
      set({ info });
    },
    invite: () => run(() => auth.createInvite()),
    remove: async id => {
      const info = await run(() => auth.removeMember(id));
      if (info) set({ info });
    },
    leave: async () => {
      const info = await run(() => auth.leavePlan());
      if (info) set({ info });
      await get().refresh();
    },
    redeem: async code => {
      const r = await run(() => auth.redeemCode(code));
      if (!r) return null;
      set({ info: { plan: r.plan, members: r.members, owner: r.owner, gifts: r.gifts } });
      await get().refresh();
      return r;
    },
    waitForPayment: async () => {
      set({ confirming: true });
      const before = await auth.fetchPlan().catch(() => null);
      const seen = (i: PlanInfo | null) => `${i?.plan?.plan ?? ''}:${i?.plan?.until ?? ''}:${i?.gifts.length ?? 0}`;
      let changed = false;
      for (let n = 0; n < 15 && !changed; n++) {
        await new Promise(r => setTimeout(r, 2000));
        const now = await auth.fetchPlan().catch(() => null);
        changed = !!now && seen(now) !== seen(before);
      }
      await get().refresh();
      set({ confirming: false });
      return changed;
    },
    refresh: async () => {
      await get().load();
      const isSuper = await checkEntitlement().catch(() => useSettings.getState().isSuper);
      if (useSettings.getState().isSuper !== isSuper) useSettings.getState().set({ isSuper });
    },
  };
});
